import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Actions, ofType, createEffect } from "@ngrx/effects";
import { of } from "rxjs";
import {
	tap,
	map,
	switchMap,
	exhaustMap,
	catchError,
	take,
	delay,
	first,
	mergeMap,
} from "rxjs/operators";
import { forkJoin } from "rxjs";
import * as lodash from "lodash";
import { Store, select } from "@ngrx/store";
import * as fromRoot from "@app-stores/reducers";
import * as AuthAction from "../actions/auth.action";
import { CrmUser, Credentials } from "@app-models/index";
import { AuthService } from "@app-services/auth.service";

@Injectable()
export class AuthEffects {
	constructor(
		private actions$: Actions,
		private router: Router,
		private _store: Store<fromRoot.AppState>,
		private authService: AuthService
	) {}
	loginWithToken$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AuthAction.AuthActionTypes.LoginWithToken),
			exhaustMap((action: AuthAction.LoginWithToken) =>
				this.authService.loginWithToken().pipe(
					map((res) => {
						if (res && res["access_token"]) {
							this.fetchAndSaveCountryTranslations(res);
							return new AuthAction.LoginSuccess(res, true, true);
						} else {
							return new AuthAction.LoginFailure({
								error: {
									message:
										"Token Expired. Please login again.",
								},
							});
						}
					}),
					catchError((error) =>
						of(new AuthAction.LoginFailure(error))
					)
				)
			),
			tap(() => {})
		)
	);

	login$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AuthAction.AuthActionTypes.Login),
			exhaustMap((action: AuthAction.Login) =>
				this.authService.login(action.payload).pipe(
					map((res) => {
						if (res && res["access_token"]) {
							this.fetchAndSaveCountryTranslations(res);
							return new AuthAction.LoginSuccess(
								res,
								action.rememberMe,
								false
							);
						} else {
							return new AuthAction.LoginFailure({
								error: { message: "Login Failure" },
							});
						}
					}),
					catchError((error) =>
						of(new AuthAction.LoginFailure(error))
					)
				)
			),
			tap(() => {})
		)
	);

	refreshToken$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AuthAction.AuthActionTypes.RefreshToken),
			exhaustMap((action: AuthAction.RefreshToken) =>
				this.authService.refreshToken(action.payload).pipe(
					map((res) => {
						if (res) return new AuthAction.RefreshTokenSuccess(res);
						else return new AuthAction.RefreshTokenFailure(null);
					}),
					catchError((error) =>
						of(new AuthAction.RefreshTokenFailure(error))
					)
				)
			)
		)
	);

	getUserDetail$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AuthAction.AuthActionTypes.GetUserDetail),
			exhaustMap((action: AuthAction.GetUserDetail) =>
				this.authService.getUserInfo().pipe(
					map((res) => {
						return new AuthAction.GetUserDetailSuccess(res);
					}),
					catchError((error) =>
						of(new AuthAction.GetUserDetailFailure(error))
					)
				)
			)
		)
	);

	// @Effect({ dispatch: false })
	loginRedirect$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					AuthAction.AuthActionTypes.LoginRedirect,
					AuthAction.AuthActionTypes.Logout
				),
				tap((authed) => {
					this.router.navigate(["/login"]);
				})
			),
		{ dispatch: false }
	);

	// @Effect()
	register$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AuthAction.AuthActionTypes.Register),
			map((action: AuthAction.Register) => action.payload),
			exhaustMap((info) =>
				this.authService.register(info.user).pipe(
					map((res) => {
						if (lodash.get(res, "success", null)) {
							return new AuthAction.RegisterSuccess({
								user: res as CrmUser,
								loginRequired: info.loginRequired,
							});
						} else {
							return new AuthAction.RegisterFailure(res);
						}
					}),
					catchError((error) => {
						return of(new AuthAction.RegisterFailure(error));
					})
				)
			)
		)
	);

	// @Effect()
	registerSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AuthAction.AuthActionTypes.RegisterSuccess),
			map((action: AuthAction.RegisterSuccess) => action.payload),
			map((info) => {
				if (info.loginRequired)
					return new AuthAction.Login(info.user, false);
				else return new AuthAction.RegisterFailure(null);
			})
		)
	);

	// @Effect({ dispatch: false })
	logut$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(AuthAction.AuthActionTypes.Logout),
				tap(() => this.router.navigate(["/login"]))
			),
		{ dispatch: false }
	);

	// @Effect()

	update$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AuthAction.AuthActionTypes.Update),
			exhaustMap((action: AuthAction.Update) =>
				this.authService.updateUser(action.payload).pipe(
					map((result) => {
						if (result && result["success"])
							return new AuthAction.UpdateSuccess(
								action.payload,
								action.isFreelancer
							);
						else
							return new AuthAction.UpdateFailure(
								"Update Failure"
							);
					}),
					catchError((error) =>
						of(new AuthAction.UpdateFailure(error))
					)
				)
			)
		)
	);

	// @Effect()
	updateSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AuthAction.AuthActionTypes.UpdateSuccess),
			exhaustMap((action: AuthAction.UpdateSuccess) =>
				this.authService.getUserInfo().pipe(
					map((res) => new AuthAction.GetUserDetailSuccess(res)),
					catchError((error) =>
						of(new AuthAction.GetUserDetailFailure(error))
					)
				)
			)
		)
	);

	// @Effect()
	changePassword$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AuthAction.AuthActionTypes.ChangePassword),
			map((action: AuthAction.ChangePassword) => action.payload),
			exhaustMap((data: any) =>
				this.authService.changePassword(data).pipe(
					map((authState) => {
						return new AuthAction.ChangePasswordSuccess(data);
					}),
					catchError((error) =>
						of(new AuthAction.ChangePasswordFailure(error))
					)
				)
			)
		)
	);
	// @Effect()
	// verifyEmail = this.actions$.pipe(
	// 	ofType(AuthAction.AuthActionTypes.VerifyEmail),
	// 	map((action: AuthAction.VerifyEmail) => action.payload),
	// 	exhaustMap((data) =>
	// 		this.authService.getEmailConfirm(data)
	// 		.pipe(
	// 			map(authState => {
	// 				return new AuthAction.VerifyEmailSuccess({email:data.email});
	// 			}),
	// 			catchError(error => of(new AuthAction.VerifyEmailFailure(error)))
	// 		)
	// 	)
	// );
	fetchAndSaveCountryTranslations(res) {
		if (!res["user"] || !res["user"]["uuid"]) {
			return;
		}
		this.authService
			.getCountryTranslations(res["user"]["uuid"])
			.subscribe((result) => {
				if (!result.data) {
					return;
				}
				localStorage.setItem(
					"countryTranslations",
					JSON.stringify(result.data)
				);
			});
	}
}